import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAmazonProfile } from '../contexts/AmazonProfileContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BuildingStorefrontIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import {
  filterStoreGroups,
  findStoreGroupForProfile,
  groupStoresByName,
  readStoreRegionPick,
  resolveProfileForStoreGroup,
  writeStoreRegionPick,
} from '../utils/storeUtils';
import { positionPanelBelow } from '../utils/floatingPanelPosition';
import { useIsMobileViewport } from '../hooks/useIsMobileViewport';
import useDebouncedValue from '../hooks/useDebouncedValue';

function marketplaceLabel(profile, { compact = false } = {}) {
  if (!profile) return 'Marketplace';
  const cc = profile.countryCode || profile.region || '—';
  if (compact) return cc;
  const cur = profile.currencyCode || profile.currency || '—';
  return `${cc} • ${cur}`;
}

function StoreSelector({ layout = 'default', showStoreName = true }) {
  const {
    selectedProfile,
    profiles,
    selectProfile,
    profilesHasMore,
    loadMoreProfiles,
    searchStores,
  } = useAmazonProfile();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [isStoreOpen, setIsStoreOpen] = useState(false);
  const [isRegionOpen, setIsRegionOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 280);
  const [serverSearchProfiles, setServerSearchProfiles] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [regionPick, setRegionPick] = useState(() => readStoreRegionPick());
  const storeBtnRef = useRef(null);
  const regionBtnRef = useRef(null);
  const [storePanelPos, setStorePanelPos] = useState(null);
  const [regionPanelPos, setRegionPanelPos] = useState(null);
  const isMobileViewport = useIsMobileViewport();

  const updateStorePanelPos = () => {
    if (!storeBtnRef.current || isMobileViewport) {
      setStorePanelPos({ mobileSheet: true });
      return;
    }
    const r = storeBtnRef.current.getBoundingClientRect();
    const pos = positionPanelBelow(r, {
      preferredWidth: Math.min(Math.max(r.width, 320), 420),
      minWidth: 280,
      align: 'start',
    });
    setStorePanelPos(pos);
  };

  const updateRegionPanelPos = () => {
    if (!regionBtnRef.current || isMobileViewport) {
      setRegionPanelPos({ mobileSheet: true });
      return;
    }
    const r = regionBtnRef.current.getBoundingClientRect();
    const pos = positionPanelBelow(r, {
      preferredWidth: 260,
      minWidth: 200,
      align: 'end',
    });
    setRegionPanelPos(pos);
  };

  useEffect(() => {
    if (!isStoreOpen) {
      setServerSearchProfiles(null);
      return undefined;
    }
    const term = debouncedSearch.trim();
    if (!term) {
      setServerSearchProfiles(null);
      return undefined;
    }
    let cancelled = false;
    setSearchLoading(true);
    searchStores(term, { limit: 80 })
      .then((items) => {
        if (!cancelled) setServerSearchProfiles(items);
      })
      .catch(() => {
        if (!cancelled) setServerSearchProfiles([]);
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isStoreOpen, debouncedSearch, searchStores]);

  const profilesForPicker = serverSearchProfiles ?? profiles;

  const storeGroups = useMemo(() => {
    if (serverSearchProfiles) {
      return groupStoresByName(profilesForPicker);
    }
    return filterStoreGroups(groupStoresByName(profilesForPicker), { query: searchQuery });
  }, [profilesForPicker, searchQuery, serverSearchProfiles]);

  const selectedGroup = useMemo(
    () => findStoreGroupForProfile(profiles, selectedProfile?.profileId),
    [profiles, selectedProfile?.profileId]
  );

  const hasMultipleMarketplaces = Boolean(selectedGroup?.hasMultipleRegions);

  useLayoutEffect(() => {
    if (!isStoreOpen) {
      setStorePanelPos(null);
      return undefined;
    }
    updateStorePanelPos();
    const onReflow = () => updateStorePanelPos();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [isStoreOpen, storeGroups.length, isMobileViewport]);

  useLayoutEffect(() => {
    if (!isRegionOpen) {
      setRegionPanelPos(null);
      return undefined;
    }
    updateRegionPanelPos();
    const onReflow = () => updateRegionPanelPos();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [isRegionOpen, selectedGroup?.id, isMobileViewport]);

  useEffect(() => {
    writeStoreRegionPick(regionPick);
  }, [regionPick]);

  useEffect(() => {
    if (!selectedProfile || !selectedGroup) return;
    const id = String(selectedProfile.profileId);
    setRegionPick((prev) => {
      if (prev[selectedGroup.id] === id) return prev;
      return { ...prev, [selectedGroup.id]: id };
    });
  }, [selectedProfile?.profileId, selectedGroup?.id]);

  const closeAll = () => {
    setIsStoreOpen(false);
    setIsRegionOpen(false);
  };

  const applyProfile = (profile, { reason } = {}) => {
    if (!profile) return;
    const same = selectedProfile
      && String(selectedProfile.profileId) === String(profile.profileId);
    if (same) return;

    selectProfile(profile);
    if (reason === 'marketplace') {
      toast.success(`Marketplace: ${marketplaceLabel(profile)}`, { duration: 2500 });
    }
    if (!profile.isConnected) {
      toast('Viewing cached data — reconnect to refresh from Amazon', { icon: 'ℹ️' });
    }
  };

  const handleStoreGroupSelect = (group) => {
    const profile = resolveProfileForStoreGroup(group, { selectedProfile, regionPick });
    if (!profile) return;
    setRegionPick((prev) => ({ ...prev, [group.id]: String(profile.profileId) }));
    applyProfile(profile);
    setIsStoreOpen(false);
    setSearchQuery('');
    if (window.location.pathname === '/stores') {
      navigate('/dashboard');
    }
  };

  const handleMarketplaceSelect = (profile) => {
    if (!selectedGroup) return;
    setRegionPick((prev) => ({ ...prev, [selectedGroup.id]: String(profile.profileId) }));
    applyProfile(profile, { reason: 'marketplace' });
    setIsRegionOpen(false);
  };

  if (!user) return null;

  const panelBorder = isDarkMode ? 'border-slate-700 bg-slate-800' : 'bg-white border-slate-200';
  const itemHover = isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50';
  const textPrimary = isDarkMode ? 'text-slate-200' : 'text-slate-900';
  const textMuted = isDarkMode ? 'text-slate-400' : 'text-slate-500';

  const isHeader = layout === 'header';
  const storeTitle = selectedGroup?.name || selectedProfile?.name || 'Select store';
  const compactMarketplace = isHeader && isMobileViewport;

  const mobileSheetShell = (title, onClose, children) => (
    <div
      className={`fixed inset-x-0 bottom-0 z-[250] flex max-h-[min(85vh,28rem)] flex-col rounded-t-2xl border-t shadow-2xl safe-area-pb ${panelBorder}`}
      role="dialog"
      aria-label={title}
    >
      <div className={`flex shrink-0 items-center justify-between gap-2 px-4 py-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
        <h3 className={`text-sm font-semibold ${textPrimary}`}>{title}</h3>
        <button
          type="button"
          onClick={onClose}
          className={`rounded-lg px-2.5 py-1 text-xs font-medium ${isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}
        >
          Done
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
    </div>
  );

  return (
    <div
      className={
        isHeader
          ? 'flex items-center gap-1 min-w-0 shrink max-w-full'
          : 'flex items-center gap-2 min-w-0 w-full max-w-xl'
      }
    >
      {/* Who — store */}
      <div className={isHeader ? 'relative min-w-0 shrink' : 'relative min-w-0 flex-1'}>
        <button
          ref={storeBtnRef}
          type="button"
          onClick={() => {
            setIsRegionOpen(false);
            setIsStoreOpen((o) => !o);
          }}
          title={!showStoreName ? storeTitle : undefined}
          className={`flex items-center gap-2 rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 border ${
            isHeader && !showStoreName ? 'p-2' : 'px-3 py-2 w-full'
          } ${
            isDarkMode
              ? 'bg-slate-800 border-slate-700 hover:bg-slate-700 hover:border-orange-500/30 focus:ring-orange-500'
              : 'bg-white border-slate-300 hover:bg-slate-50 focus:ring-orange-500 focus:border-orange-500'
          }`}
          aria-expanded={isStoreOpen}
          aria-haspopup="listbox"
          aria-label={`Store: ${storeTitle}`}
        >
          <BuildingStorefrontIcon className={`h-5 w-5 flex-shrink-0 ${isDarkMode ? 'text-orange-400' : 'text-slate-600'}`} />
          {showStoreName && (
            <>
              <div className="flex-1 text-left min-w-0">
                <div className={`text-sm font-medium truncate ${textPrimary}`}>
                  {storeTitle}
                </div>
                {!isHeader && (
                  <div className={`text-xs truncate ${textMuted}`}>
                    {selectedProfile ? 'Tap to change store' : 'Choose your store'}
                  </div>
                )}
              </div>
              <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 ${textMuted}`} />
            </>
          )}
        </button>

        {isStoreOpen && storePanelPos && typeof document !== 'undefined' && createPortal(
          storePanelPos.mobileSheet ? mobileSheetShell('Select store', closeAll, (
            <>
            <div className={`p-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <p className={`text-xs mb-2 ${textMuted}`}>
                Country / currency is changed with the globe button next to the store.
              </p>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="search"
                  placeholder="Search stores..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`block w-full pl-8 pr-3 py-2 text-sm rounded-md focus:ring-2 border ${
                    isDarkMode
                      ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400 focus:ring-orange-500'
                      : 'bg-white border-slate-300 text-slate-900 focus:ring-orange-500'
                  }`}
                />
              </div>
            </div>
            <div className="max-h-[50vh] overflow-y-auto">
              {storeGroups.length > 0 ? (
                storeGroups.map((group) => {
                  const isSelected = group.stores.some(
                    (s) => String(s.profileId) === String(selectedProfile?.profileId)
                  );
                  const preview = resolveProfileForStoreGroup(group, { selectedProfile, regionPick });
                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => handleStoreGroupSelect(group)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 text-left border-b last:border-b-0 transition-colors ${
                        isDarkMode ? 'border-slate-700' : 'border-slate-100'
                      } ${
                        isSelected
                          ? (isDarkMode ? 'bg-orange-500/10 border-l-2 border-l-orange-500' : 'bg-orange-50 border-l-2 border-l-orange-500')
                          : itemHover
                      }`}
                    >
                      <div className="flex items-center min-w-0 flex-1">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 ${
                          isDarkMode ? 'bg-orange-500/20' : 'bg-orange-100'
                        }`}>
                          <BuildingStorefrontIcon className={`h-4 w-4 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                        </div>
                        <div className="min-w-0">
                          <div className={`text-sm font-medium truncate ${textPrimary}`}>{group.name}</div>
                          <div className={`text-xs ${textMuted}`}>
                            {group.hasMultipleRegions
                              ? `${group.stores.length} marketplaces`
                              : marketplaceLabel(preview)}
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <CheckIcon className={`h-5 w-5 flex-shrink-0 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                      )}
                    </button>
                  );
                })
              ) : (
                <div className={`px-3 py-6 text-center text-sm ${textMuted}`}>
                  {searchQuery ? 'No stores match your search' : 'No stores available'}
                </div>
              )}
            </div>
            </>
          )) : (
          <div
            className={`fixed rounded-xl shadow-2xl z-[250] flex flex-col overflow-hidden border ${panelBorder}`}
            style={{
              top: storePanelPos.top,
              left: storePanelPos.left,
              width: storePanelPos.width,
              maxHeight: storePanelPos.maxHeight,
            }}
            role="listbox"
          >
            <div className={`p-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <h3 className={`text-sm font-semibold mb-2 ${textPrimary}`}>Select store</h3>
              <p className={`text-xs mb-2 ${textMuted}`}>
                Country / currency is changed with the button next to the store name.
              </p>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="search"
                  placeholder="Search stores..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`block w-full pl-8 pr-3 py-2 text-sm rounded-md focus:ring-2 border ${
                    isDarkMode
                      ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400 focus:ring-orange-500'
                      : 'bg-white border-slate-300 text-slate-900 focus:ring-orange-500'
                  }`}
                />
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {storeGroups.length > 0 ? (
                storeGroups.map((group) => {
                  const isSelected = group.stores.some(
                    (s) => String(s.profileId) === String(selectedProfile?.profileId)
                  );
                  const preview = resolveProfileForStoreGroup(group, { selectedProfile, regionPick });

                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => handleStoreGroupSelect(group)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 text-left border-b last:border-b-0 transition-colors ${
                        isDarkMode ? 'border-slate-700' : 'border-slate-100'
                      } ${
                        isSelected
                          ? (isDarkMode ? 'bg-orange-500/10 border-l-2 border-l-orange-500' : 'bg-orange-50 border-l-2 border-l-orange-500')
                          : itemHover
                      }`}
                    >
                      <div className="flex items-center min-w-0 flex-1">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 ${
                          isDarkMode ? 'bg-orange-500/20' : 'bg-orange-100'
                        }`}>
                          <BuildingStorefrontIcon className={`h-4 w-4 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                        </div>
                        <div className="min-w-0">
                          <div className={`text-sm font-medium truncate ${textPrimary}`}>{group.name}</div>
                          <div className={`text-xs ${textMuted}`}>
                            {group.hasMultipleRegions
                              ? `${group.stores.length} marketplaces`
                              : marketplaceLabel(preview)}
                            {!preview?.isConnected && (
                              <span className="text-amber-600 font-medium ml-1">· Cached</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <CheckIcon className={`h-5 w-5 flex-shrink-0 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                      )}
                    </button>
                  );
                })
              ) : (
                <div className={`px-3 py-6 text-center text-sm ${textMuted}`}>
                  {searchLoading
                    ? 'Searching…'
                    : searchQuery
                      ? 'No stores match your search'
                      : 'No stores available'}
                </div>
              )}
            </div>

            <div className={`px-3 py-2 border-t text-xs text-center space-y-1 ${textMuted} ${
              isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-slate-200 bg-slate-50'
            }`}>
              <div>
                {serverSearchProfiles
                  ? `${storeGroups.length} search result(s)`
                  : `${storeGroups.length} shown · ${groupStoresByName(profiles).length} loaded`}
              </div>
              {!serverSearchProfiles && profilesHasMore && (
                <button
                  type="button"
                  className="text-orange-600 font-semibold hover:underline"
                  onClick={() => loadMoreProfiles()}
                >
                  Load more stores
                </button>
              )}
            </div>
          </div>
          ),
          document.body
        )}
      </div>

      {/* Where — marketplace chip (always visible) */}
      <div className="relative flex-shrink-0">
        <button
          ref={regionBtnRef}
          type="button"
          disabled={!selectedProfile}
          onClick={() => {
            if (!selectedProfile) return;
            setIsStoreOpen(false);
            if (hasMultipleMarketplaces) {
              setIsRegionOpen((o) => !o);
            }
          }}
          className={`flex items-center gap-1.5 rounded-lg border text-sm font-medium transition-colors focus:outline-none focus:ring-2 ${
            isHeader ? 'px-2 py-2' : 'px-3 py-2'
          } ${
            !selectedProfile
              ? (isDarkMode ? 'border-slate-700 text-slate-500 bg-slate-800/50' : 'border-slate-200 text-slate-400 bg-slate-50')
              : hasMultipleMarketplaces
                ? (isDarkMode
                  ? 'bg-slate-800 border-slate-600 text-orange-100 hover:bg-slate-700 hover:border-orange-500/40 focus:ring-orange-500'
                  : 'bg-white border-slate-300 text-slate-800 hover:bg-orange-50 hover:border-orange-400 focus:ring-orange-500')
                : (isDarkMode
                  ? 'bg-slate-800/80 border-slate-700 text-slate-300 cursor-default'
                  : 'bg-slate-50 border-slate-200 text-slate-600 cursor-default')
          }`}
          title={
            hasMultipleMarketplaces
              ? 'Change marketplace'
              : 'This store has one marketplace'
          }
          aria-expanded={isRegionOpen}
          aria-haspopup="listbox"
        >
          <GlobeAltIcon className={`h-4 w-4 flex-shrink-0 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
          <span className="whitespace-nowrap tabular-nums">
            {selectedProfile ? marketplaceLabel(selectedProfile, { compact: compactMarketplace }) : '—'}
          </span>
          {hasMultipleMarketplaces && (
            <ChevronDownIcon className={`h-3.5 w-3.5 ${textMuted}`} />
          )}
        </button>

        {isRegionOpen && hasMultipleMarketplaces && selectedGroup && regionPanelPos && typeof document !== 'undefined' && createPortal(
          regionPanelPos.mobileSheet
            ? mobileSheetShell(`Marketplace · ${selectedGroup.name}`, closeAll, (
              <ul className="py-1">
                {selectedGroup.stores.map((profile) => {
                  const active = String(profile.profileId) === String(selectedProfile?.profileId);
                  return (
                    <li key={profile.profileId}>
                      <button
                        type="button"
                        onClick={() => handleMarketplaceSelect(profile)}
                        className={`w-full flex items-center justify-between px-4 py-3 text-left text-sm ${itemHover} ${
                          active ? (isDarkMode ? 'bg-orange-500/10' : 'bg-orange-50') : ''
                        }`}
                      >
                        <span className={textPrimary}>
                          {marketplaceLabel(profile)}
                          {!profile.isConnected && (
                            <span className="text-amber-600 text-xs ml-1">cached</span>
                          )}
                        </span>
                        {active && (
                          <CheckIcon className={`h-4 w-4 shrink-0 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ))
            : (
          <div
            className={`fixed rounded-xl shadow-2xl z-[250] overflow-hidden border ${panelBorder}`}
            style={{
              top: regionPanelPos.top,
              left: regionPanelPos.left,
              width: regionPanelPos.width,
              maxHeight: regionPanelPos.maxHeight,
            }}
            role="listbox"
          >
            <div className={`px-3 py-2 border-b text-xs font-medium ${textMuted} ${
              isDarkMode ? 'border-slate-700' : 'border-slate-200'
            }`}>
              Marketplace for {selectedGroup.name}
            </div>
            <ul className="max-h-56 overflow-y-auto py-1">
              {selectedGroup.stores.map((profile) => {
                const active = String(profile.profileId) === String(selectedProfile?.profileId);
                return (
                  <li key={profile.profileId}>
                    <button
                      type="button"
                      onClick={() => handleMarketplaceSelect(profile)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 text-left text-sm ${itemHover} ${
                        active ? (isDarkMode ? 'bg-orange-500/10' : 'bg-orange-50') : ''
                      }`}
                    >
                      <span className={textPrimary}>
                        {marketplaceLabel(profile)}
                        {!profile.isConnected && (
                          <span className="text-amber-600 text-xs ml-1">cached</span>
                        )}
                      </span>
                      {active && (
                        <CheckIcon className={`h-4 w-4 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
            ),
          document.body
        )}
      </div>

      {(isStoreOpen || isRegionOpen) && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[240] bg-slate-900/20 dark:bg-black/40" onClick={closeAll} aria-hidden="true" />,
        document.body
      )}
    </div>
  );
}

export default StoreSelector;
